import {
  alpha,
  Box,
  Chip,
  Paper,
  useColorScheme,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";
import {
  EnigmaHeatMapEntry,
  RawHeatMapCell,
  RawHeatMapGroup,
  RawHeatMapRow,
} from "../../pages/Engimas/EnigmaTimelinesHeatMapChart/EnigmaTimelinesHeatMapChart.types";
import ContextMenu from "../ContextMenu";
import { LoadingGuard } from "../LoadingGuard";
import {
  HeatMapOption,
  ProcessedCell,
  ProcessedRow,
} from "./HeatMapChart.types";

export interface HeatMapChartProps {
  options: HeatMapOption[];
  rows?: RawHeatMapRow[]; // ✅ Ora riceve Raw invece di Processed
  loading?: boolean;
  totalItems?: number;
  emptyColor?: string; // ✅ Colore per celle vuote
  cacheKey: string; // ✅ Chiave per caching personalizzato
  onCellClick?: (
    filterKeys: string[],
    groupLabel: string,
    timeLabel: string
  ) => void;
  onViewDetails?: (entries: EnigmaHeatMapEntry[], description: string) => void;
}

export function HeatMapChart({
  options,
  rows,
  loading = false,
  totalItems,
  emptyColor = "#f5f5f5",
  onCellClick,
  onViewDetails,
}: HeatMapChartProps) {
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [selectedFilterKeys, setSelectedFilterKeys] = useState<string[] | null>(
    null
  );

  const processedRows = useMemo(
    () => processRows(rows ?? [], totalItems, emptyColor, selectedFilterKeys),
    [rows, totalItems, emptyColor, selectedFilterKeys]
  );

  const toggleSelection = (slotKey: string | null, filterKeys: string[]) => {
    if (selectedSlot === slotKey) {
      setSelectedSlot(null);
      setSelectedFilterKeys(null);
    } else {
      setSelectedSlot(slotKey);
      setSelectedFilterKeys(filterKeys);
    }
  };

  useEffect(() => {
    toggleSelection(null, []);
  }, [rows]);

  const handleCellClick = (cell: ProcessedCell) => {
    if (!cell.count) return;

    toggleSelection(cell.key, cell.filterKeys);
    onCellClick?.(
      cell.filterKeys,
      cell.group.label,
      options[cell.timeIndex].label
    );
  };

  const handleViewDetails = (cell: ProcessedCell) => {
    onViewDetails?.(
      cell.entries,
      `${cell.group.label} - ${options[cell.timeIndex].label}`
    );
  };

  return (
    <LoadingGuard isLoading={loading}>
      <HeatMapGrid
        options={options}
        processedRows={processedRows}
        selectedSlot={selectedSlot}
        totalItems={totalItems}
        onCellClick={handleCellClick}
        onViewDetails={handleViewDetails}
      />
    </LoadingGuard>
  );
}

// Grid component for the heatmap
interface HeatMapGridProps {
  options: HeatMapOption[];
  processedRows: ProcessedRow[];
  selectedSlot: string | null;
  totalItems?: number;
  onCellClick: (cell: ProcessedCell) => void;
  onViewDetails: (cell: ProcessedCell) => void;
}

function HeatMapGrid({
  options,
  processedRows,
  selectedSlot,
  totalItems,
  onCellClick,
  onViewDetails,
}: HeatMapGridProps) {
  return (
    <Paper
      elevation={1}
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        gap: 0.125, // 1px equivalent
        backgroundColor: "background.paper",
        p: 1.25,
        borderRadius: 1,
        mb: 1.25,
      }}
    >
      <HeatMapHeader options={options} />
      {processedRows?.map((row, index) => (
        <HeatMapRow
          key={row.group.id}
          row={row}
          index={index}
          selectedSlot={selectedSlot}
          totalItems={totalItems}
          onCellClick={onCellClick}
          onViewDetails={onViewDetails}
        />
      ))}
    </Paper>
  );
}

// Header component
interface HeatMapHeaderProps {
  options: HeatMapOption[];
}

function HeatMapHeader({ options }: HeatMapHeaderProps) {
  const theme = useTheme();

  return (
    <>
      {options.map((option, index) => (
        <Box
          key={`header-${index}`}
          sx={{
            fontSize: "0.5625rem", // 9px
            textAlign: "center",
            p: 0.125,
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.secondary,
            fontWeight: 500,
            borderRadius: 0.5,
          }}
        >
          {option.label}
        </Box>
      ))}
    </>
  );
}

// Row component
interface HeatMapRowProps {
  row: ProcessedRow;
  index: number;
  selectedSlot: string | null;
  totalItems?: number;
  onCellClick: (cell: ProcessedCell) => void;
  onViewDetails: (cell: ProcessedCell) => void;
}

function HeatMapRow({
  row,
  index,
  selectedSlot,
  totalItems,
  onCellClick,
  onViewDetails,
}: HeatMapRowProps) {
  const theme = useTheme();
  const { mode } = useColorScheme();

  return (
    <>
      {/* Group label */}
      <Box
        key={`label-${row.group.id}`}
        sx={{
          gridColumn: "1 / -1",
          display: "flex",
          justifyContent: "center",
          mt: index > 0 ? 0.625 : 0,
          mb: 0.25,
        }}
      >
        <Chip
          label={row.group.label}
          size="small"
          sx={{
            backgroundColor: row.group.color.hex,
            color:
              mode === "dark"
                ? theme.palette.common.black
                : theme.palette.common.white,
            fontWeight: 600,
            fontSize: "0.6875rem", // 11px
            height: 24,
          }}
        />
        {row.avgTime && (
          <Box
            sx={{
              fontSize: "0.6875rem", // 11px, stesso del chip
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            ({formatSecondsToDDHHMMSS(row.avgTime)})
          </Box>
        )}
      </Box>

      {/* Cells */}
      {row.cells.map((cell) => (
        <HeatMapCell
          key={cell.key}
          cell={cell}
          isSelected={selectedSlot === cell.key}
          totalItems={totalItems}
          onCellClick={onCellClick}
          onViewDetails={onViewDetails}
        />
      ))}
    </>
  );
}

// Cell component
interface HeatMapCellProps {
  cell: ProcessedCell;
  isSelected: boolean;
  totalItems?: number;
  onCellClick: (cell: ProcessedCell) => void;
  onViewDetails: (cell: ProcessedCell) => void;
}

function HeatMapCell({
  cell,
  isSelected,
  totalItems,
  onCellClick,
  onViewDetails,
}: HeatMapCellProps) {
  const theme = useTheme();

  // ✅ Usa funzioni pure con nomi autoesplicativi
  const displayText = calculateDisplayText(cell.count, totalItems);
  const textColor = determineTextColor(
    cell.intensity,
    cell.group.color.needContrastOnHighIntensity,
    theme
  );

  return (
    <ContextMenu
      items={[
        {
          label: "Vedi informazioni partite",
          onClick: () => onViewDetails(cell),
        },
      ]}
    >
      <Box
        onClick={() => onCellClick(cell)}
        sx={{
          backgroundColor: cell.color,
          height: 25,
          borderRadius: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.5625rem", // 9px
          fontWeight: "bold",
          color: textColor,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          outline: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : "none",
          transform: isSelected ? "scale(1.1)" : "scale(1)",
          zIndex: isSelected ? 1 : 0,
          cursor: cell.count > 0 ? "pointer" : "default",
          transition: theme.transitions.create(["transform", "outline"], {
            duration: theme.transitions.duration.shortest,
          }),
          "&:hover":
            cell.count > 0
              ? {
                  transform: "scale(1.05)",
                  boxShadow: theme.shadows[2],
                }
              : {},
        }}
      >
        {displayText}
      </Box>
    </ContextMenu>
  );
}

const calculateIntensity = (count: number, maxCount: number): number =>
  maxCount > 0 ? count / maxCount : 0;

const calculatePercentage = (count: number, totalItems?: number): string =>
  totalItems ? ((count / totalItems) * 100).toFixed(1) : "0";

function getCellColor(
  [h, s, _l]: [number, number, number],
  intensity: number
): string {
  const lightness = 95 - intensity * 60; // Range from 95% to 35%
  return `hsl(${h}, ${s}%, ${lightness}%)`;
}

function calcRowAvgTime(cells: RawHeatMapCell[]): number {
  let totalTime = 0;
  let totalEntries = 0;
  for (const cell of cells) {
    for (const entry of cell.entries) {
      totalTime += entry.timeFromStart;
      totalEntries++;
    }
  }
  return totalEntries > 0 ? totalTime / totalEntries : 0;
}

// ✅ Funzione pura trasferita dal componente (SINGLE RESPONSIBILITY)
const processRows = (
  rows: RawHeatMapRow[],
  totalItems?: number,
  emptyColor: string = "#f5f5f5",
  selectedFilterKeys?: string[] | null
): ProcessedRow[] => {
  // console.time("transformRawToProcessedRows - Main thread");
  // console.debug("Processing raw rows", {
  //   rows,
  //   totalItems,
  //   selectedFilterKeys,
  // });

  const result: ProcessedRow[] = [];

  for (const row of rows) {
    const filteredCells = [];
    let maxCount = 0;
    for (const cell of row.cells) {
      const filteredCell = filterCellEntries(cell, selectedFilterKeys);
      maxCount = Math.max(maxCount, filteredCell.count);
      filteredCells.push(filteredCell);
    }

    const parsedCells = [];
    for (const cell of filteredCells) {
      parsedCells.push(
        formatCell(cell, row.group, maxCount, totalItems, emptyColor)
      );
    }

    result.push({
      group: {
        id: row.group.id,
        label: row.group.label,
        color: row.group.color,
      },
      cells: parsedCells,
      hasData: row.hasData,
      avgTime: calcRowAvgTime(row.cells),
    });
  }

  // console.timeEnd("transformRawToProcessedRows - Main thread");
  return result;
};

function formatCell(
  cell: RawHeatMapCell,
  group: RawHeatMapGroup,
  maxCount: number,
  totalItems?: number,
  emptyColor: string = "#f5f5f5"
): ProcessedCell {
  // ✅ Usa funzioni pure con nomi autoesplicativi
  const intensity = calculateIntensity(cell.count, maxCount);

  const color =
    cell.count > 0 ? getCellColor(group.color.hsl, intensity) : emptyColor;

  const percentage = calculatePercentage(cell.count, totalItems);

  return {
    key: cell.key,
    count: cell.count,
    intensity,
    color,
    percentage,
    filterKeys: cell.filterKeys,
    entries: cell.entries,
    timeIndex: cell.timeIndex,
    group: {
      id: group.id,
      label: group.label,
      color: group.color,
    },
  };
}

const calculateDisplayText = (count: number, totalItems?: number): string =>
  count > 0 && totalItems ? `${((count / totalItems) * 100).toFixed(2)}%` : "";

const determineTextColor = (
  intensity: number,
  needContrastOnHighIntensity: boolean,
  theme: any
): string => {
  if (intensity === 0) return theme.palette.text.primary;

  const needsWhiteText =
    intensity > 0.6 || (needContrastOnHighIntensity && intensity > 0.5);

  return needsWhiteText
    ? theme.palette.common.white
    : theme.palette.text.primary;
};

function filterCellEntries(
  cell: RawHeatMapCell,
  filterKeys?: string[] | null
): RawHeatMapCell {
  if (!filterKeys?.length) return cell;
  let entries: EnigmaHeatMapEntry[] = [];
  for (const entry of cell.entries) {
    if (filterKeys.includes(entry.filterKey)) {
      entries.push(entry);
    }
  }
  return {
    ...cell,
    entries,
    count: entries.length,
  };
}
